import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';

export type TabItemProps = {
  label: string;
  href: string;
  // Domain identifier for the tab (matched against the URL's :tab param).
  // Named `id` rather than `key` to avoid shadowing React's reserved key prop.
  id: string;
  disabled: boolean;
  Icon: React.ElementType;
};

export type TabGroup = {
  label: string;
  tabs: Array<TabItemProps>;
};

export type Orientation = 'vertical' | 'horizontal';

type TabItemPropsInternal = TabItemProps & {
  selected: boolean;
  orientation: Orientation;
};

const TabItem = (props: TabItemPropsInternal) => {
  const { label, href, selected, Icon, orientation } = props;
  const navigate = useNavigate();

  // Horizontal reads as a tab strip: the current tab is marked by a brand-
  // coloured underline, which is what "you are here" looks like in a top bar.
  // Colour alone was doing that job before, in green — the app's success
  // colour — which said "done" rather than "current".
  if (orientation === 'horizontal') {
    return (
      <li>
        <button
          type="button"
          aria-current={selected ? 'page' : undefined}
          onClick={() => navigate(href)}
          className={clsx(
            'group relative flex items-center gap-2 px-3 pt-1.5 pb-2.5 rounded-t-md',
            'transition-colors duration-150',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-sky focus-visible:ring-offset-1',
            selected
              ? 'text-sky font-semibold'
              : 'text-grey-medium hover:text-grey-dark hover:bg-grey-super_light'
          )}
        >
          <Icon
            className={clsx(
              'w-4 h-4 shrink-0 transition-colors duration-150',
              selected
                ? 'text-sky'
                : 'text-grey-medium_light group-hover:text-grey-medium'
            )}
          />
          <span className="whitespace-nowrap text-sm leading-none">{label}</span>
          {/* Sits on the container's border line so the active tab reads as
              joined to the panel below it. */}
          <span
            aria-hidden="true"
            className={clsx(
              'absolute left-2 right-2 -bottom-px h-0.5 rounded-full transition-colors duration-150',
              selected ? 'bg-sky' : 'bg-transparent group-hover:bg-grey-light'
            )}
          />
        </button>
      </li>
    );
  }

  return (
    <li className={clsx('font-semibold')}>
      <button
        className="w-full text-left flex space-x-3 items-center"
        onClick={() => navigate(href)}
      >
        {/* was text-gray-400 — the palette defines `grey`, not `gray`, so that
            class generated nothing and these icons had no colour at all. */}
        <Icon className="w-4 shrink-0 text-grey-medium_light" />
        <p
          className={clsx(
            'text-grey-dark whitespace-nowrap',
            selected && 'text-green'
          )}
        >
          {label}
        </p>
      </button>
    </li>
  );
};

export type TabsProps = {
  tabGroups: Array<TabGroup>;
  selectedTab: string;
  // 'vertical' is the original left-column layout. 'horizontal' lays the same
  // tabs out as a top bar, which frees the whole grid width for the content
  // pane — the admin tables need it far more than the nav does.
  orientation?: Orientation;
};

export const Sidebar = (props: TabsProps) => {
  const { tabGroups, selectedTab, orientation = 'vertical' } = props;

  if (orientation === 'horizontal') {
    return (
      <nav aria-label="Admin paneel" className="w-full">
        <div className="flex items-center gap-3 mb-2">
          <p className="text-sky text-xs font-bold uppercase tracking-widest">
            Admin panel
          </p>
          <span aria-hidden="true" className="h-px flex-1 bg-grey-light" />
        </div>

        {/* Groups keep the meaning the vertical nav carried, as a quiet eyebrow
            above each cluster rather than a heading competing with the page
            title. The shared bottom border is the rail the active tab sits on. */}
        {/* No vertical dividers between groups. The strip wraps at narrow
            widths, and a divider is only meaningful *between* two groups on
            the same line — CSS cannot know where the wrap falls, so a wrapped
            row began with a hairline hanging in front of it. The eyebrow
            labels already separate the groups; spacing does the rest. */}
        <ul className="flex flex-wrap items-end gap-x-6 gap-y-1 border-b border-grey-light">
          {tabGroups.map((group) => (
            <li key={group.label} className="flex flex-col">
              <span className="px-3 pb-0.5 text-[10px] font-bold uppercase tracking-wider text-grey-medium_light">
                {group.label}
              </span>
              <ul className="flex flex-wrap items-end">
                {group.tabs.map((item) => (
                  <TabItem
                    key={item.id}
                    {...item}
                    orientation="horizontal"
                    selected={selectedTab === item.id}
                  />
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </nav>
    );
  }

  return (
    <section>
      <div>
        <p className="text-sky text-lg font-bold">ADMIN PANEL</p>

        <div className="w-full pt-8 flex items-center">
          <div className="border-b border-grey-light w-full" />
        </div>
      </div>
      <ul className="flex flex-col mt-5">
        {tabGroups.map((group) => {
          return (
            <li key={group.label} className="flex-col space-y-1">
              <div className="flex space-x-3">
                <p className="font-bold text-lg text-grey-dark mb-3">
                  {group.label}
                </p>
              </div>
              <ul className="flex flex-col space-y-2">
                {group.tabs.map((item) => {
                  return (
                    <TabItem
                      key={item.id}
                      {...item}
                      orientation="vertical"
                      selected={selectedTab === item.id}
                    />
                  );
                })}
              </ul>

              {/* Border */}
              <div className="w-full pt-8 pb-3 flex items-center">
                <div className="border-b border-grey-light w-full" />
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
